import {
  Button,
  ButtonGroup,
  Chip,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  EditIcon,
  InfoIcon,
  LazyLoader,
  Modal,
  Row,
  Table,
  TableColumns,
  Tooltip,
} from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { useColumnsSort } from '../../hooks/useColumnsSort';
import { useConfirm } from '../../hooks/useConfirm';
import { useHiddenColumns } from '../../hooks/useHiddenColumns';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useMemorizedCacheTag } from '../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../hooks/useSelectedSchoolYear';
import { useDeleteTimetablesMutation } from '../../types/planung-graphql-client-defs';
import { showSuccessDeleteToast, showUserErrorToast } from '../../utils/toast';
import { TimetableVersionsOverviewModal } from '../TimetableVersion/TimetableVersionsOverview/TimetableVersionsOverviewModal';
import { TimetableForm } from './Forms/TimetableForm';
import { useTimetables } from './hooks/useTimetables';

type TimetableStatusToken = 'current' | 'past' | 'future' | 'none';

type TimetableStatusEntry = { token: TimetableStatusToken; label: string };

type TimetableTableType = {
  uuid: string;
  name: string;
  versions: number;
  createdAt: string;
  updatedAt: string;
  comment: string;
  /** Übersetzte Status-Texte (kommagetrennt) – wird für Sortierung/Filterung der Spalte genutzt. */
  status: string;
  /** Status-Einträge (Token + Label) zur Chip-Darstellung. */
  statusEntries: TimetableStatusEntry[];
  draft: boolean;
  hasOpenActiveVersion: boolean;
  /** Löschen bleibt gesperrt, solange noch irgendeine Version oder Sicherung am Plan hängt. */
  hasVersionsOrSnapshots: boolean;
};

/** Farben der Status-Chips. */
const STATUS_CHIP_STYLE: Record<TimetableStatusToken, { bgColor: string; color: string }> = {
  current: { bgColor: 'var(--color-success)', color: 'var(--color-white)' },
  past: { bgColor: 'var(--color-grey-lighter)', color: 'var(--color-grey-dark)' },
  future: { bgColor: 'var(--color-primary-light)', color: 'var(--color-grey)' },
  none: { bgColor: 'var(--color-grey-lightest)', color: 'var(--color-grey)' },
};

/**
 * Ermittelt den Status eines Stundenplans anhand seiner TimetableBlocks:
 * - "current": heute liegt in mindestens einem Block (gewinnt immer)
 * - "past" / "future": je nachdem ob Blocks in der Vergangenheit/Zukunft liegen
 *   (liegt beides vor, werden beide Stati ausgegeben); "future" zeigt das Startdatum ("Ab …")
 *   des frühesten zukünftigen Blocks
 * - "none": der Stundenplan steckt in keinem Block
 */
const getTimetableStatusEntries = (
  blocks: { start: string; end: string }[],
  t: (key: string, options?: Record<string, unknown>) => string,
): TimetableStatusEntry[] => {
  if (blocks.length === 0) {
    return [{ token: 'none', label: t('timetable.status.none') }];
  }
  const today = dayjs();
  const isCurrent = blocks.some(
    (b) => dayjs(b.start).isSameOrBefore(today, 'day') && dayjs(b.end).isSameOrAfter(today, 'day'),
  );
  if (isCurrent) {
    return [{ token: 'current', label: t('timetable.status.current') }];
  }
  const entries: TimetableStatusEntry[] = [];
  if (blocks.some((b) => dayjs(b.end).isBefore(today, 'day'))) {
    entries.push({ token: 'past', label: t('timetable.status.past') });
  }
  const futureBlocks = blocks.filter((b) => dayjs(b.start).isAfter(today, 'day'));
  if (futureBlocks.length > 0) {
    const earliestStart = futureBlocks.reduce(
      (min, b) => (dayjs(b.start).isBefore(dayjs(min)) ? b.start : min),
      futureBlocks[0].start,
    );
    entries.push({
      token: 'future',
      label: t('timetable.status.future', { date: dayjs(earliestStart).format('DD.MM.YYYY') }),
    });
  }
  return entries.length > 0 ? entries : [{ token: 'none', label: t('timetable.status.none') }];
};

export const TimetableTable = observer(() => {
  const isMobile = useIsMobile();
  const { pimAuthClaims } = useAuthClaims();
  const permissions = usePermissionChecker();
  const { t } = useTranslation();
  const schoolYear = useSelectedSchoolYear();

  const { columnVisibility, saveColumnVisibility } = useHiddenColumns('timetable-list', {});
  const { sorting, saveSorting } = useColumnsSort('timetable-list');

  const { confirm, ConfirmationDialog } = useConfirm();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [timetableUuid, setTimetableUuid] = useState<string | null>(null);
  const [showVersionModal, setShowVersionModal] = useState(false);

  const context = useMemorizedCacheTag('TIMETABLE');
  const schoolYearUuid = schoolYear?.uuid;

  const { timetables: loadedTimetables } = useTimetables(schoolYearUuid);

  const timetables = useMemo(() => {
    // Das Backend liefert die Pläne des Schuljahres, zuletzt geänderte zuerst.
    return loadedTimetables ?? [];
  }, [loadedTimetables]);

  const [, deleteTimetables] = useDeleteTimetablesMutation();

  //biome-ignore lint/correctness/useExhaustiveDependencies(t): suppress dependency t
  const tableColumns: TableColumns<TimetableTableType>[] = useMemo(() => {
    return [
      {
        header: t('common.name'),
        id: 'name',
        accessorKey: 'name',
        size: 250,
      },
      {
        header: t('timetableDraft.versionCount'),
        id: 'versions',
        accessorKey: 'versions',
        size: 50,
      },
      {
        header: t('common.createdAt'),
        id: 'createdAt',
        accessorKey: 'createdAt',
        type: 'date',
        size: 150,
      },
      {
        header: t('common.updatedAt'),
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        type: 'date',
        size: 150,
      },
      {
        header: t('common.comment'),
        id: 'comment',
        accessorKey: 'comment',
        size: 100,
      },
      {
        header: t('common.status'),
        id: 'status',
        accessorKey: 'status',
        size: 200,
        cell: ({ row }) => {
          return (
            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              {row.original.statusEntries.map((entry) => (
                <Chip
                  key={entry.token}
                  value={entry.label}
                  bgColor={STATUS_CHIP_STYLE[entry.token].bgColor}
                  color={STATUS_CHIP_STYLE[entry.token].color}
                />
              ))}
            </div>
          );
        },
      },
    ];
  }, []);

  //biome-ignore lint/correctness/useExhaustiveDependencies(t): suppress dependency t
  const memoizedData = useMemo((): TimetableTableType[] => {
    return timetables.map((tt) => {
      const statusEntries = getTimetableStatusEntries(tt.timetableBlocks ?? [], t);
      return {
        uuid: tt.uuid,
        statusEntries,
        status: statusEntries.map((entry) => entry.label).join(', '),
        draft: tt.draft,
        comment: tt.comment ?? '',
        createdAt: dayjs(tt.createdAt).format('DD.MM.YYYY - HH:mm'),
        name: tt.name,
        // Sicherungen (Snapshots) sind technisch auch Versionen, zählen für den Nutzer aber nicht dazu.
        versions: tt.versions.filter((v) => !v.isSnapshot).length,
        hasVersionsOrSnapshots: tt.versions.length > 0,
        updatedAt: dayjs(tt.updatedAt).format('DD.MM.YYYY - HH:mm'),
        hasOpenActiveVersion:
          tt.versions.some((v) => v.active && v.editable) &&
          tt.timetableBlocks.some((b) => dayjs(b.start).isSameOrBefore(dayjs(), 'day')),
      };
    });
  }, [timetables]);

  const handleDelete = async (uuids: string[]) => {
    const timetablesToDelete = timetables.filter((t) => uuids.includes(t.uuid) && t.draft && t.versions.length === 0);

    const tt = timetablesToDelete.map((tt) => tt.name);

    await confirm({
      onConfirm: async () => {
        const resp = await deleteTimetables({ uuids: timetablesToDelete.map((tt) => tt.uuid) }, context);
        if (resp.error) {
          showUserErrorToast({ error: resp.error });
        }
        if (!resp.error) showSuccessDeleteToast();
      },
      message: t('timetableDraft.deleteConfirm', {
        timetables: tt?.join(', '),
        count: tt?.length,
      }),
    });
  };

  const handleEdit = (draftRow: Row<TimetableTableType>) => {
    setTimetableUuid(draftRow.original.uuid);
    setIsModalOpen(true);
  };

  function closeModal() {
    setTimetableUuid(null);
    setIsModalOpen(false);
  }

  function handleShowVersionModal(timetable: TimetableTableType) {
    setTimetableUuid(timetable.uuid);
    setShowVersionModal(true);
  }

  function handleHideVersionModal() {
    setTimetableUuid(null);
    setShowVersionModal(false);
  }

  //biome-ignore lint/correctness/useExhaustiveDependencies(t): suppress dependency t
  //biome-ignore lint/correctness/useExhaustiveDependencies(handleDelete): suppress dependency handleDelete
  const createActionItems = useCallback((row: Row<TimetableTableType>): DropdownMenuItem[] => {
    return [
      {
        label: t('common.delete'),
        type: 'default',
        color: 'error',
        onClick: async () => {
          await handleDelete([row.original.uuid]);
        },
        disabled: !row.original.draft || row.original.hasVersionsOrSnapshots,
      },
    ];
  }, []);

  return (
    <>
      <Table<TimetableTableType>
        showBorderRadius
        showShadow
        // mobil Karten statt Tabelle: „Versionen“ steht so direkt am Eintrag statt ganz rechts (mobile-plan.md E14)
        isDesktop={!isMobile}
        showVisibility
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={saveColumnVisibility}
        sorting={sorting}
        onSortingChange={saveSorting}
        columns={tableColumns}
        data={memoizedData}
        showActionBar
        actionBarSettings={{ showAddButton: permissions?.canWriteTimetable() }}
        isOnWhite={false}
        printerSettings={{
          headline: pimAuthClaims.getProfile()?.organization.name,
          subline: `${t('timetable.title', { count: 2 })} - ${t('common.schoolYear')} ${schoolYear?.shortName}`,
          filename: `${t('timetable.title', { count: 2 })}_${schoolYear?.shortName}`,
        }}
        onAddClick={() => {
          setIsModalOpen(true);
        }}
        lastColWidth='160px'
        lastCol={(row) => {
          return (
            <ButtonGroup className='ml-4'>
              <Button hierarchy='secondary' onClick={() => handleShowVersionModal(row.original)}>
                {t('pinboard.versions')}
              </Button>
              {row.original.hasOpenActiveVersion ? (
                <Button
                  hierarchy='secondary'
                  onClick={() => handleShowVersionModal(row.original)}
                  icon={
                    row.original.hasOpenActiveVersion ? (
                      <Tooltip content={t('timetableVersion.editableVersionHint')}>
                        <InfoIcon color='var(--color-warning)' />
                      </Tooltip>
                    ) : undefined
                  }
                />
              ) : (
                <Button
                  hierarchy={'secondary'}
                  onClick={() => {
                    handleEdit(row);
                  }}
                  icon={<EditIcon className='small' />}
                  disabled={!permissions?.canWriteTimetable() || !row.original.draft}
                />
              )}
              <Dropdown
                noPadding
                disabled={!permissions?.canWriteTimetable()}
                trigger={
                  <Button
                    hierarchy='secondary'
                    disabled={!permissions?.canWriteTimetable()}
                    icon={<DotsHorizontalIcon className='small' />}
                  />
                }
              >
                <DropdownMenu data={createActionItems(row)} />
              </Dropdown>
            </ButtonGroup>
          );
        }}
      />
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={timetableUuid ? t('timetableDraft.edit') : t('timetableDraft.add')}
      >
        <Suspense fallback={<LazyLoader embedded={true} />}>
          <TimetableForm timetableUuid={timetableUuid} selectAllClasses onClose={closeModal} />
        </Suspense>
      </Modal>
      <TimetableVersionsOverviewModal
        timetableUuid={timetableUuid}
        draftName={memoizedData.find((tt) => tt.uuid === timetableUuid)?.name ?? ''}
        isOpen={showVersionModal && !!timetableUuid}
        onClose={() => handleHideVersionModal()}
      />

      <ConfirmationDialog />
    </>
  );
});
